import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  useTheme,
  Grid,
  useMediaQuery,
  CircularProgress,
  Alert,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import {
  Info as InfoIcon ,
  WarningAmber as WarningAmberIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ExpandMore,
  ExpandLess,
  Edit as EditIcon
} from '@mui/icons-material';
import { useLocation, useOutletContext, useNavigate } from 'react-router-dom';
import { OutletContextType } from '../root';
import {
  eventManagementInitialize,
  updateEvent,
  changePublic,
  approval,
  deny,
  deleteParticipant,
  paymentRegister,
  csvDownload,
  teamDownload,
  eventDelete,
  publishMessage,
  advanceNotice,
  todayDrinkApproval,
  registParticipant,
  updateParticipants
} from '../../api/api';
import { ReserveStatus } from '../../api/unit/ReserveStatus';
import { Sex } from '../../api/unit/Sex';
import { Flag } from '../../api/unit/Flag';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

/**
 * 体育館情報
 */
type ArenaInfo = {
  id: string,
  name: string
};

/**
 * 支出情報
 */
type PaymentInfo = {
  description: string,
  amount: number
};

/**
 * 参加者情報
 */
export interface ParticipantData {
  user_id: string;
  user_name: string;
  sex: Sex,
  age: string,
  paypay_id: string,
  paypay_name: string,
  b_participation_flag: Flag;
  d_participation_flag: Flag;
  reserve_status: ReserveStatus;
  question_answer: string;
  message: string;
  update_date: string;
  manual_flag?: boolean; // 手動登録フラグ
}

/**
 * イベントID
 */
let eventId = '';

export const EventManagement: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useOutletContext<OutletContextType>();
  const [reloadTime, setReloadTime] = useState<Date>();
  const [arenaOptions, setArenaOptions] = useState<ArenaInfo[]>([]);
  const [paymentRows, setPaymentRows] = useState<PaymentInfo[]>([]);
  const [paymentRowsBk, setPaymentRowsBk] = useState<PaymentInfo[]>([]);
  const [participantsData, setParticipantsData] = useState<ParticipantData[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<ParticipantData | null>(null);
  const [isProgress, setIsProgress] = useState<boolean>(false);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState<boolean>(false);
  const [openDenyDialog, setOpenDenyDialog] = useState<boolean>(false);
  const [openAdvanceNoticeDialog, setOpenAdvanceNoticeDialog] = useState<boolean>(false);
  const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);
  const [openPublicSwitchDialog, setOpenPublicSwitchDialog] = useState<boolean>(false);
  const [oepnParticipantDeleteDialog, setOpenParticipantDeleteDialog] = useState<boolean>(false);
  const [openPaymentDetailDialog, setOpenPaymentDetailDialog] = useState<boolean>(false);
  const [openResultDialog, setOpenResultDialog] = useState<boolean>(false);
  const [processResultMessage, setProcessResultMessage] = useState<string>('');
  const [approveUserId, setApproveUserId] = useState<string>('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const [denyMessage, setDenyMessage] = useState('');
  const [advanceNotifyUserId, setAdvanceNoticeUserId] = useState<string>('');
  const [advanceNotifyMessage, setAdvanceNoticeMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedUserIdList, setSelectedUserIdList] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    user_id: '',
    event_name: '',
    event_date: '',
    b_start_time: '',
    b_end_time: '',
    d_start_time: '',
    d_end_time: '',
    b_entry_fee: 0,
    d_entry_fee: 0,
    arena_id: '',
    restaurant: '',
    restaurant_url: '',
    question_content: '',
    event_content: '',
    belongings: '',
    notes: '',
    meeting_time: '',
    capacity_num: 0,
    man_capacity_num: 0,
    woman_capacity_num: 0,
    memo: '',
    public_flag: false
  });

  // レスポンシブ対応
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // この画面に渡されたパラメータを取得する
  const location = useLocation();
  eventId = location.state.event_id;

  /**
   * 初期表示処理
   */
  useEffect(() => {
    if (userId && eventId) {
        eventManagementInitialize(userId, eventId).then(response => {
            // イベント情報を設定
            var data = {
                user_id: response.userId,
                event_name: response.eventName,
                event_date: response.eventDate,
                b_start_time: response.bStartTime,
                b_end_time: response.bEndTime,
                d_start_time: response.dStartTime,
                d_end_time: response.dEndTime,
                b_entry_fee: response.bEntryFee,
                d_entry_fee: response.dEntryFee,
                arena_id: response.arenaId,
                restaurant: response.restaurant,
                restaurant_url: response.restaurantUrl,
                question_content: response.questionContent,
                event_content: response.eventContent,
                belongings: response.belongings,
                notes: response.notes,
                meeting_time: response.meetingTime,
                capacity_num: response.capacityNum,
                man_capacity_num: response.manCapacityNum,
                woman_capacity_num: response.womanCapacityNum,
                memo: response.memo,
                public_flag: response.publicFlag === '1',
                manual_flag: false
            };
            setFormData(data);

            // 体育館リストを設定
            const newArenaInfo = response.arenaList?.map((arenaEntity: any) => ({
                id: arenaEntity.arenaId,
                name: arenaEntity.arenaName,
            }));
            setArenaOptions(newArenaInfo);

            // 収支明細を設定
            const newPaymentInfo = response.paymentList?.map((paymentEntity: any) => ({
              description: paymentEntity.description,
              amount: paymentEntity.amount
            }));
            setPaymentRows(newPaymentInfo);
            setPaymentRowsBk(newPaymentInfo);

            // 参加者一覧を設定
            const newParticipantsData = response.tkgReserve0210DetailsObjectLot?.tkgReserve0210DetailsObjectList?.map((participantEntity: any) => ({
                user_id: participantEntity.userId,
                user_name: participantEntity.userName,
                sex: new Sex(participantEntity.sex),
                age: participantEntity.age,
                paypay_id: participantEntity.paypayId,
                paypay_name: participantEntity.paypayName,
                b_participation_flag: new Flag(participantEntity.bParticipationFlag),
                d_participation_flag: new Flag(participantEntity.dParticipationFlag),
                reserve_status: new ReserveStatus(participantEntity.reserveStatus),
                question_answer: participantEntity.questionAnswer,
                message: participantEntity.message,
                update_date: participantEntity.updateDate
            }));
            setParticipantsData(newParticipantsData);
        });
    }
  }, [userId, location.state.event_id, reloadTime]);

  /**
   * イベント削除処理
   */
  const handleDeleteEvent = () => {
    setOpenPublicSwitchDialog(false); 
    // イベント削除処理を実施する
    eventDelete(userId, eventId).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
    setOpenDialog(false);
  };

  /**
   * イベント公開・非公開処理
   */
  const handlePublicSwitchEvent = () => {
    setOpenPublicSwitchDialog(false); 
    // 公開・非公開処理を実施する
    changePublic(userId, eventId).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * 募集配信ボタンを押したときの処理
   */
  const handleBroadcastEvent = () => {
    setOpenPublishDialog(false);
    var message = (document.getElementById('publishMessage') as HTMLTextAreaElement).value;
    message = message.replace(/\r\n|\r/g, "\n"); // 改行コードを統一
    // メッセージを配信する
    publishMessage(userId, message).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * フォームの内容が変更されたら入力された情報を取得する
   * @param e DOM
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: type === 'checkbox' ? checked : value };
      return updatedFormData;
    });
  };
  
  /**
   * 体育館プルダウンの動作
   */
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /**
   * CSVダウンロード処理
   */
  const handleCsvDownload = () => {
    setIsProgress(true);
    csvDownload(userId, eventId);
    setIsProgress(false);
  }

  /**
   * チーム分け表ダウンロード処理
   */
  const handleTeamDownload = () => {
    setIsProgress(true);
    teamDownload(userId, eventId);
    setIsProgress(false);
  }

  /**
   * 支払い・キャンセル承認処理
   */
  const handleApproval = () => {
    setOpenApprovalDialog(false);
    const message = approvalMessage.replace(/\r\n|\r/g, "\n"); // 改行コードを統一
    // 承認処理を実施する
    approval(userId, approveUserId, eventId, message).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  }

  /**
   * 支払い・キャンセル却下処理
   */
  const handleDeny = () => {
    setOpenDenyDialog(false);
    const message = denyMessage.replace(/\r\n|\r/g, "\n"); // 改行コードを統一
    // 否認処理を実施する
    deny(userId, approveUserId, eventId, message).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  }

  /**
   * 支払い・キャンセル承認処理
   */
  const handleAdvanceNotice = () => {
    setOpenAdvanceNoticeDialog(false);
    const message = approvalMessage.replace(/\r\n|\r/g, "\n"); // 改行コードを統一
    // 承認処理を実施する
    advanceNotice(userId, advanceNotifyUserId, eventId, message).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  }

  /**
   * 飲み会当日参加承認処理
   */
  const handleTodayDrinkApproval = (targetUserId : string) => {
    // 飲み会当日参加承認処理を実施する
    todayDrinkApproval(userId, targetUserId, eventId).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * 行選択チェックボックスを押下した時の処理
   */
  const handleCheckboxClick = (user_id: string) => {
    // selectedUserIdListにuser_idが含まれていれば削除、含まれていなければ追加する
    if (selectedUserIdList.includes(user_id)) {
      setSelectedUserIdList(prevList => prevList?.filter(id => id !== user_id));
    } else {
      setSelectedUserIdList(prevList => [...prevList, user_id]);
    }
  };

  /**
   * 参加者削除ボタンを押下した時の処理
   */
  const handleDeleteParticipant = () => {
    setOpenParticipantDeleteDialog(false);
    // 削除処理を実施する
    deleteParticipant(userId, selectedUserIdList, eventId).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * 参加者追加ボタンを押下した時の処理
   */
  const handleParticipantInsert = () => {
    // 参加者追加処理を実施する
    const newParticipant: ParticipantData = {
      user_id: '',
      user_name: '',
      sex: new Sex(Sex.Man),
      age: '',
      paypay_id: '',
      paypay_name: '',
      b_participation_flag: new Flag(Flag.OFF),
      d_participation_flag: new Flag(Flag.OFF),
      reserve_status: new ReserveStatus(ReserveStatus.NoGoing),
      question_answer: '',
      message: '',
      update_date: new Date().toISOString(),
      manual_flag: true
    }
    setParticipantsData([...participantsData, newParticipant]);
  }

  /**
   * イベント内容更新ボタンを押下したときの処理
   */
  const handleSubmit = () => {
    setIsProgress(true);
    // 必須項目の検証
    const isError = (
        !formData.event_name
        && !formData.event_date
        && !formData.meeting_time
        && !formData.capacity_num
    )

    if (isError) {
      setErrorMessage('必須項目をすべて入力してください。');
      setIsProgress(false);
      return;
    } else {
      setErrorMessage('');
    }

    // 日付と時刻を結合して新しいフォーマットに変換
    const formatDateTime = (date: string, time: string) => {
      const [year, month, day] = date.split('-');
      const [hour, minute] = time.split(':');
      return `${year}/${month}/${day} ${hour}:${minute}:00`;
    };

    const formattedData = {
      ...formData,
      question_content: formData.question_content?.replace(/\r\n|\r/g, "\n")?.replace(/\n/g, '\\n'),
      event_content: formData.event_content?.replace(/\r\n|\r/g, "\n")?.replace(/\n/g, '\\n'),
      belongings: formData.belongings?.replace(/\r\n|\r/g, "\n")?.replace(/\n/g, '\\n'),
      notes: formData.notes?.replace(/\r\n|\r/g, "\n")?.replace(/\n/g, '\\n'),
      memo: formData.memo?.replace(/\r\n|\r/g, "\n")?.replace(/\n/g, '\\n'),
      b_start_time: formData.b_start_time ? formatDateTime(formData.event_date, formData.b_start_time) : null,
      b_end_time: formData.b_end_time ? formatDateTime(formData.event_date, formData.b_end_time) : null,
      d_start_time: formData.d_start_time ? formatDateTime(formData.event_date, formData.d_start_time) : null,
      d_end_time: formData.d_end_time ? formatDateTime(formData.event_date, formData.d_end_time) : null,
      meeting_time: formData.meeting_time ? formatDateTime(formData.event_date, formData.meeting_time) : null,
    };

    formData.user_id = userId;

    // イベント更新処理を実施する
    updateEvent(userId, eventId, formattedData).then(response => {
      setIsProgress(false);
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  }

  const handleAddPaymentRow = () => {
    setPaymentRows([...paymentRows, { description: '', amount: 0 }]);
  };
  
  const handleInputChange = (index: number, field: 'description' | 'amount', value: string) => {
    setPaymentRows(
      paymentRows?.map((row, i) =>
        i === index ? { ...row, [field]: field === 'amount' ? Number(value) : value } : row
      )
    );
  };
  
  const handlePaymentSave = () => {
    const requestData = {
      "user_id": userId,
      "event_id": eventId,
      "payment_list": paymentRows
    }
    console.log(requestData);
    // 支払い登録処理を実施する
    paymentRegister(requestData).then(response => {
      setIsProgress(false);
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * 参加者追加処理でレコードの内容を更新する処理
   *
   * @param index インデクス
   * @param field フィールド名
   * @param value 設定する値
   */
  const handleUserInputChange = (index: number, field: keyof ParticipantData, value: any) => {
    const updatedUsers = [...participantsData];
    updatedUsers[index] = {
      ...updatedUsers[index],
      [field]: value,
    };
    setParticipantsData(updatedUsers);
  };

  /**
   * 参加者追加処理（登録ボタン押下時処理）
   *
   * @param data 参加者情報
   */
  const handleRegistParticipantData = (data: ParticipantData) => {
    setIsProgress(true);
    registParticipant(userId, eventId, data).then(response => {
      setIsProgress(false);
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  /**
   * 参加者編集行の保存処理
   *
   * @param participant 参加者情報
   */
  const handleSaveRow = (participant: ParticipantData) => {
    // Find the original participant data to check if there are changes
    const originalParticipant = editingRowData;
    if (!originalParticipant || originalParticipant.reserve_status.getCode() === participant.reserve_status.getCode()) {
      // No changes, just exit edit mode
      setEditingRowId(null);
      setEditingRowData(null);
      return;
    }

    setIsProgress(true);

    updateParticipants(userId, participant.user_id, eventId, participant.reserve_status.getCode()).then(response => {
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
      setIsProgress(false);
      setEditingRowId(null);
      setEditingRowData(null);
    });
  };

  /**
   * 画面コンテンツ.
   */
  return (
    <>
    <Box sx={{ paddingTop: '10vh', display: 'flex', justifyContent: 'space-between' }}>
      {/* 左端：ポータルに戻るボタン */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon sx={{ transition: 'transform 0.3s ease' }} />}
        onClick={() => navigate("/mng/portal")}
        sx={{
          ':hover': {
            backgroundColor: '#1565c0',
            color: 'white',
          },
        }}
      >
        ポータルに戻る
      </Button>

      {/* 右端：リロードボタン */}
      <Button
        variant="outlined"
        color="secondary"
        startIcon={
          <RefreshIcon sx={{ transition: 'transform 0.3s ease' }} />
        }
        onClick={() => setReloadTime(new Date())}
        sx={{
          ':hover': {
            backgroundColor: 'secondary.main',
            color: 'white',
            '& .MuiSvgIcon-root': {
              transform: 'rotate(180deg)',
            },
          },
        }}
      >
        リロード
      </Button>
    </Box>
    <Box sx={{ p: 2 }}>
      {/* タイトル */}
      <Typography variant="h4" gutterBottom>
        {formData.event_name}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {formData.event_content}
      </Typography>

      {/* イベントの状態ラベル */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Typography
          sx={{
            padding: '6px 12px',
            borderRadius: '8px',
            backgroundColor: formData.public_flag ? '#f8c2c2' : '#c2d9f8',
            color: formData.public_flag ? 'red' : 'blue',
          }}
        >
          {formData.public_flag ? '公開中' : '非公開'}
        </Typography>
      </Box>

      {/* イベント操作フォームエリア */}
      <Button variant="outlined" onClick={() => setOpenForm(!openForm)} sx={{ mb: 2, marginRight: '10px'  }}>
        イベント編集{openForm ? <ExpandLess /> : <ExpandMore />} {/* 展開/折りたたみアイコンの切り替え */}
      </Button>

    {/* 募集配信ボタン */}
    {formData.public_flag ? (
        <Button
            variant="contained"
            color="success"
            sx={{ mb: 2, marginRight: '10px' }}
            onClick={() => setOpenPublishDialog(true)}
        >
            募集配信
        </Button>
    ) : (<></>)}

     {/* 処理中アイコン */}
      {isProgress ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant="body1"sx={{ padding: '10px' }}>
                処理中…
            </Typography>
        </Box>
      ) : (<></>)}

      <Collapse in={openForm} >
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingBottom: '5vh' }}>
          <Paper sx={{ padding: 3, width: '100%', maxWidth: '600px', borderRadius: '16px', backgroundColor: '#f3f4f6' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                イベント編集
            </Typography>

            {/* 公開・非公開ボタン */}
            <FormControl sx={{ marigin: '10px', marginBottom: '20px' }}>
                <Button
                    onClick={() => setOpenPublicSwitchDialog(true)} 
                    variant={formData.public_flag ? "outlined" : "contained"}
                    color={formData.public_flag ? "primary" : "warning"}
                    sx={{ marginRight: '10px' }}
                >
                    {formData.public_flag ? '非公開にする' : '公開する'}
                </Button>
            </FormControl>

            {/* イベント削除ボタン */}
            {!formData.public_flag ? (
                <Button
                    variant="contained"
                    color="error"
                    sx={{ mb: 2, marginRight: '10px' }}
                    onClick={() => setOpenDialog(true)}
                >
                    イベント削除
                </Button>
            ) : (<></>)}

        {/* エラーメッセージ */}
        {errorMessage ? (
          <Alert severity="error" sx={{ marginBottom: '1rem' }}>
            {errorMessage}
          </Alert>
        ) : (<></>)}

        <Grid container spacing={2}>
          {/* Event Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="イベント名"
              variant="outlined"
              name="event_name"
              value={formData.event_name}
              onChange={handleChange}
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Event Date */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="イベント開催日"
              variant="outlined"
              name="event_date"
              type="date"
              value={formData.event_date}
              onChange={handleChange}
              required
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Basketball Start Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="バスケ開始時刻"
              variant="outlined"
              name="b_start_time"
              type="time"
              value={formData.b_start_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Basketball End Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="バスケ終了時刻"
              variant="outlined"
              name="b_end_time"
              type="time"
              value={formData.b_end_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Drinking Start Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="飲み会開始時刻"
              variant="outlined"
              name="d_start_time"
              type="time"
              value={formData.d_start_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Drinking End Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="飲み会終了時刻"
              variant="outlined"
              name="d_end_time"
              type="time"
              value={formData.d_end_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Meeting Time */}
          <Grid item xs={6}>
            <TextField
              label="集合時間"
              variant="outlined"
              name="meeting_time"
              type="time"
              value={formData.meeting_time}
              onChange={handleChange}
              required
              fullWidth
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Capacity */}
          <Grid item xs={6}>
            <TextField
              label="キャパシティ"
              variant="outlined"
              name="capacity_num"
              value={formData.capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* 男性キャパシティ */}
          <Grid item xs={6}>
            <TextField
              label="男性キャパシティ"
              variant="outlined"
              name="man_capacity_num"
              value={formData.man_capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* 女性キャパシティ */}
          <Grid item xs={6}>
            <TextField
              label="女性キャパシティ"
              variant="outlined"
              name="woman_capacity_num"
              value={formData.woman_capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Basketball Entry Fee */}
          <Grid item xs={6}>
            <TextField
              label="バスケ参加費"
              variant="outlined"
              name="b_entry_fee"
              value={formData.b_entry_fee}
              onChange={handleChange}
              fullWidth
              type="number"
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Drinking Entry Fee */}
          <Grid item xs={6}>
            <TextField
              label="飲み会参加費"
              variant="outlined"
              name="d_entry_fee"
              value={formData.d_entry_fee}
              onChange={handleChange}
              fullWidth
              type="number"
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Arena */}
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>体育館</InputLabel>
              <Select
                name="arena_id"
                value={formData.arena_id}
                onChange={handleSelectChange}
                sx={{ backgroundColor: '#fff' }}
              >
                <MenuItem key='' value='' />
                {arenaOptions && arenaOptions.length > 0 ? arenaOptions?.map((arena) => (
                  <MenuItem key={arena.id} value={arena.id}>
                    {arena.name}
                  </MenuItem>
                )) : <></>}
              </Select>
            </FormControl>
          </Grid>

          {/* Restaurant */}
          <Grid item xs={12}>
            <TextField
              label="飲み会場所"
              variant="outlined"
              name="restaurant"
              value={formData.restaurant}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Restaurant URL */}
          <Grid item xs={12}>
            <TextField
              label="飲み会場所 URL"
              variant="outlined"
              name="restaurant_url"
              value={formData.restaurant_url}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* belongings */}
          <Grid item xs={12}>
            <TextField
              label="持ち物"
              variant="outlined"
              name="belongings"
              value={formData.belongings}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* notes */}
          <Grid item xs={12}>
            <TextField
              label="注意事項"
              variant="outlined"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Event Content */}
          <Grid item xs={12}>
            <TextField
              label="イベント内容"
              variant="outlined"
              name="event_content"
              value={formData.event_content}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Question Content */}
          <Grid item xs={12}>
            <TextField
              label="質問内容"
              variant="outlined"
              name="question_content"
              value={formData.question_content}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* MEMO */}
          <Grid item xs={12}>
            <TextField
              label="管理者用メモ"
              variant="outlined"
              name="memo"
              value={formData.memo}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={handleSubmit}
              sx={{
                backgroundColor: '#FF9800',
                ':hover': {
                  backgroundColor: '#FFB74D',
                },
                width: "50%"
              }}
            >
              イベント内容更新
            </Button>
          </Grid>
        </Grid>
        </Paper>
        </Box>
      </Collapse>

    {/* 人数集計エリア */}
    {isMobile ? (
      <>
        <Paper sx={{ padding: 3, width: '80%', maxWidth: '600px', borderRadius: '16px', backgroundColor: '#f3f4f6', marginBottom: '20px' }}>
          <Typography>バスケ：{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm()).length}人（女性{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isWomen()).length}人 男性{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isMan()).length}人）</Typography>
          <Typography>飲み会：{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm()).length}人（女性{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isWomen()).length}人 男性{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isMan()).length}人）</Typography>
        </Paper>
        <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => {setPaymentRowsBk(paymentRows);setOpenPaymentDetailDialog(true);}}
            sx={{
                width: "50%",
                marginRight: '10px'
            }}
        >
            支出の詳細
        </Button>

      </>
    ) : (
        <Box sx={{ padding: '15px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Paper sx={{ padding: 3, width: '48%', maxWidth: '600px', borderRadius: '16px', backgroundColor: '#f3f4f6', marginBottom: '20px' }}>
            <Typography variant="h6" sx={{ textAlign: 'center', marginBottom: '15px', fontWeight: 'bold' }}>人数集計</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 'bold' }}>バスケ参加：</Typography>
                  <Typography>{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm()).length}人（{participantsData?.filter(p => p.b_participation_flag.isON() && !p.reserve_status.isConfirm()).length}人）</Typography>
                  <Typography>女性{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isWomen()).length}人 男性{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isMan()).length}人</Typography>
              </Grid>
              <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 'bold' }}>確定：</Typography>
                  <Typography>{participantsData?.filter(p => p.reserve_status.isConfirm()).length}人</Typography>
                  <Typography>女性{participantsData?.filter(p => p.reserve_status.isConfirm() && p.sex.isWomen()).length}人 男性{participantsData?.filter(p => p.reserve_status.isConfirm() && p.sex.isMan()).length}人</Typography>
              </Grid>
              <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 'bold' }}>飲み会参加：</Typography>
                  <Typography>{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm()).length}人（{participantsData?.filter(p => p.d_participation_flag.isON() && !p.reserve_status.isConfirm()).length}人）</Typography>
                  <Typography>女性{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isWomen()).length}人 男性{participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm() && p.sex.isMan()).length}人</Typography>
              </Grid>
              <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 'bold' }}>支払い確認待ち：</Typography>
                  <Typography>{participantsData?.filter(p => p.reserve_status.isPayApprovalWait()).length}人</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* 収支計算エリア */}
          <Paper sx={{ padding: 3, width: '48%', maxWidth: '600px', borderRadius: '16px', backgroundColor: '#f3f4f6', marginBottom: '20px', marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textAlign: 'center', marginBottom: '15px', fontWeight: 'bold' }}>収支計算</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography sx={{ fontWeight: 'bold' }}>収入：</Typography>
                    <Typography sx={{ fontWeight: 'bold', color: 'green' }}>
                    {(participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.b_entry_fee + (participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.d_entry_fee}円
                    （+{participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isPayApprovalWait()).length * formData.b_entry_fee + participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isPayApprovalWait()).length * formData.d_entry_fee}円見込み）
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography sx={{ fontWeight: 'bold' }}>収支：</Typography>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            color: (participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.b_entry_fee + (participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.d_entry_fee - paymentRows?.reduce((sum, row) => sum + row.amount, 0) > 0 ? "blue" : "red",
                            textTransform: 'uppercase'
                        }}
                    >
                        {(participantsData?.filter(p => p.b_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.b_entry_fee + (participantsData?.filter(p => p.d_participation_flag.isON() && p.reserve_status.isConfirm()).length) * formData.d_entry_fee - paymentRows?.reduce((sum, row) => sum + row.amount, 0)}円
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography sx={{ fontWeight: 'bold' }}>支出：</Typography>
                    <Typography sx={{ fontWeight: 'bold', color: 'red' }}>▲{paymentRows?.reduce((sum, row) => sum + row.amount, 0)}円</Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => {setPaymentRowsBk(paymentRows);setOpenPaymentDetailDialog(true);}}
                        sx={{
                            width: isMobile ? "100%" : "50%",
                            marginRight: '10px'
                        }}
                    >
                        支出の詳細
                    </Button>
                </Grid>
            </Grid>
        </Paper>
      </Box>
    )}

      {/* 参加者削除ボタン */}
      {selectedUserIdList.length > 0 ? (
        <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => setOpenParticipantDeleteDialog(true)}
            sx={{
                ':hover': {
                    backgroundColor: 'red',
                    color: "white",
                },
                width: isMobile ? "50%" : "10%",
                marginRight: '10px'
            }}
        >
            削除する
        </Button>
      ) : (<></>)}

      {/* 参加者追加ボタン */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon sx={{transition: 'transform 0.3s ease'}} />}
        onClick={handleParticipantInsert}
        sx={{
            ':hover': {
                backgroundColor: 'primary.main',
                color: "white",
            },
            width: isMobile ? "50%" : "10%",
            marginRight: '10px'
        }}
      >
          参加者追加
      </Button>

    {/* CSVダウンロードボタン */}
    <Button variant="contained" color="info" sx={{ mb: 2, marginRight: '10px' }} onClick={handleCsvDownload}>
        CSVダウンロード
    </Button>

    {/* チーム分け表ダウンロードボタン */}
    <Button variant="contained" color="info" sx={{ mb: 2, marginRight: '10px' }} onClick={handleTeamDownload}>
        チーム分け表ダウンロード
    </Button>

      {/* 参加者一覧テーブル */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed', display: isMobile ? 'grid' : '', minWidth: 650}}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30px'}}/>
              <TableCell sx={{ width: '30px'}}/>
              <TableCell>名前</TableCell>
              <TableCell sx={{ textAlign: 'center', width: '80px'}}>性別</TableCell>
              <TableCell sx={{ textAlign: 'center', width: '80px'}}>年齢</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>バスケ参加</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>飲み会参加</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>ステータス</TableCell>
              {formData.question_content ? (<TableCell sx={{ width: isMobile ? '100px' : '200px'}}>{isMobile ? '質問の回答' : formData.question_content}</TableCell>) : (<></>)}
              <TableCell sx={{ textAlign: 'center' }}>メッセージ</TableCell>
              <TableCell sx={{ textAlign: 'center', width: '100px' }}>更新日時</TableCell>
              <TableCell sx={{ width: '120px' }}>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participantsData?.map((participant, index) => (
                <TableRow key={participant.user_id} sx={{ backgroundColor: selectedUserIdList.includes(participant.user_id) ? "#bbdefb" : "white" }}>
                <TableCell sx={{ textAlign: 'center' }}>
                    <Checkbox onClick={() => handleCheckboxClick(participant.user_id)} checked={selectedUserIdList.includes(participant.user_id)} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                <TableCell>
                  {participant.manual_flag ? <><TextField label="名前" value={participant.user_name} onChange={(e) => handleUserInputChange(index, "user_name", e.target.value)} /></> : <>{participant.user_name}</>}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {participant.manual_flag ? <><FormControl fullWidth><InputLabel>性別</InputLabel><Select value={participant.sex?.getCode()} onChange={(e) => handleUserInputChange(index, "sex", new Sex(e.target.value))}><MenuItem value={Sex.Women}>女性</MenuItem><MenuItem value={Sex.Man}>男性</MenuItem></Select></FormControl></> : <>{participant.sex?.toString()}</>}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {participant.manual_flag ? <><TextField label="年齢" type="number" inputProps={{ step: 1, min: 20 }} value={participant.age} onChange={(e) => handleUserInputChange(index, "age", e.target.value)} /></> : <>{participant.age}</>}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {participant.manual_flag ? <><Checkbox onChange={(e) => handleUserInputChange(index, "b_participation_flag", new Flag(e.target.checked ? Flag.ON : Flag.OFF))} sx={{ '&.Mui-checked': { color: participant.b_participation_flag?.getColor() } }}/></> : <><Checkbox checked={participant.b_participation_flag?.isCheck()} sx={{ '&.Mui-checked': { color: participant.b_participation_flag?.getColor() } }}/></>}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {participant.manual_flag ? <><Checkbox onChange={(e) => handleUserInputChange(index, "d_participation_flag", new Flag(e.target.checked ? Flag.ON : Flag.OFF))} sx={{ '&.Mui-checked': { color: participant.d_participation_flag?.getColor() } }}/></> : <><Checkbox checked={participant.d_participation_flag?.isCheck()} sx={{ '&.Mui-checked': { color: participant.d_participation_flag?.getColor() } }}/></>}
                </TableCell>
                <TableCell sx={{ textAlign: 'center', backgroundColor: participant.reserve_status?.getColor() }}>
                  {(participant.manual_flag || editingRowId === participant.user_id) ? <><FormControl fullWidth><InputLabel>ステータス</InputLabel><Select value={participant.reserve_status.getCode()} onChange={(e) => handleUserInputChange(index, "reserve_status", new ReserveStatus(e.target.value))}><MenuItem value={ReserveStatus.NoGoing}>不参加</MenuItem><MenuItem value={ReserveStatus.PayApprovalWait}>支払い確認待ち</MenuItem><MenuItem value={ReserveStatus.Confirm}>確定</MenuItem><MenuItem value={ReserveStatus.CancelApprovalWait}>キャンセル承認待ち</MenuItem><MenuItem value={ReserveStatus.CancelWaitReserve}>キャンセル待ち予約中</MenuItem><MenuItem value={ReserveStatus.CancelWaitResrveNotified}>キャンセル待ち予約中_通知済</MenuItem><MenuItem value={ReserveStatus.Invalid}>無効</MenuItem></Select></FormControl></> : <>{participant.reserve_status?.getName()}</>}
                </TableCell>
                {formData.question_content ? 
                  (<TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{participant.manual_flag ? <><TextField label="質問の回答" value={participant.question_answer} onChange={(e) => handleUserInputChange(index, "question_answer", e.target.value)} /></> : <>{participant.question_answer}</>}</TableCell>)
                : (<></>)}
                <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{participant.message}</TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{participant.update_date}</TableCell>
                {/* 承認・却下ボタン */}
                {(participant?.reserve_status?.isApprovalWait() || participant?.b_participation_flag?.isONtoOFF() || participant?.d_participation_flag?.isONtoOFF()) && !participant.manual_flag ? (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                        <Button
                            variant="outlined"
                            color="success"
                            onClick={() => {setApproveUserId(participant.user_id);setOpenApprovalDialog(true);}}
                            sx={{
                            ':hover': {
                                backgroundColor: '#4caf50',
                                color: "white",
                            }
                            }}
                        >
                            承認
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {setApproveUserId(participant.user_id);setOpenDenyDialog(true);}}
                            sx={{
                            ':hover': {
                                backgroundColor: '#f44336',
                                color: "white",
                            }
                            }}
                        >
                            却下
                        </Button>
                    </Box>
                  </TableCell>
                ) : (<></>)}
                {/* 飲み会当日参加承認ボタン */}
                {participant?.d_participation_flag?.isOFFtoON() && !participant.manual_flag ? (
                  <>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '16px' }}>
                          <Button
                              variant="contained"
                              color="success"
                              onClick={() => {handleTodayDrinkApproval(participant.user_id);}}
                              sx={{
                              ':hover': {
                                  backgroundColor: '#4caf50',
                                  color: "white",
                              }
                              }}
                          >
                            飲み会当日参加承認
                        </Button>
                      </Box>
                    </TableCell>
                  </>
                ) : <></>}
                {/* 飲み会当日参加承認ボタン */}
                {participant.manual_flag ? (
                  <>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '16px' }}>
                          <Button
                              variant="outlined"
                              color="success"
                              onClick={() => {handleRegistParticipantData(participant)}}
                              sx={{
                              ':hover': {
                                  backgroundColor: 'success.main',
                                  color: "white",
                              }
                              }}
                          >
                            登録
                        </Button>
                      </Box>
                    </TableCell>
                  </>
                ) : <></>}
                {/* 繰り上がり通知ボタン */}
                {(participant?.reserve_status?.isCancelWaitReserve() || participant?.reserve_status?.isCancelWaitReserveNotified()) && !participant.manual_flag ? (
                  <>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '16px' }}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => {setAdvanceNoticeUserId(participant.user_id);setOpenAdvanceNoticeDialog(true);}}
                            sx={{
                            ':hover': {
                                backgroundColor: '#4caf50',
                                color: "white",
                            }
                            }}
                        >
                            繰り上がり通知
                        </Button>
                      </Box>
                    </TableCell>
                  </>
                ) : (<></>)}
                <TableCell>
                  {editingRowId === participant.user_id ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton onClick={() => handleSaveRow(participant)} color="success">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={() => {
                        if (editingRowData) {
                          const originalData = [...participantsData];
                          const idx = originalData.findIndex(p => p.user_id === editingRowId);
                          if (idx !== -1) {
                            originalData[idx] = editingRowData;
                            setParticipantsData(originalData);
                          }
                        }
                        setEditingRowId(null);
                        setEditingRowData(null);
                      }} color="error">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton onClick={() => {
                      setEditingRowId(participant.user_id);
                      setEditingRowData({
                        ...participant,
                        sex: new Sex(participant.sex.getCode()),
                        b_participation_flag: new Flag(participant.b_participation_flag.code),
                        d_participation_flag: new Flag(participant.d_participation_flag.code),
                        reserve_status: new ReserveStatus(participant.reserve_status.getCode()),
                      });
                    }} color="primary" disabled={participant.manual_flag}>
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 参加者削除ダイアログ */}
      <Dialog open={oepnParticipantDeleteDialog} onClose={() => setOpenParticipantDeleteDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'error.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">本当に削除しますか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
            削除操作は元に戻せません。参加者を削除する場合は削除ボタンを押下してください。
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenParticipantDeleteDialog(false)} variant="outlined" color="error">
            キャンセル
          </Button>
          <Button onClick={handleDeleteParticipant} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* イベント削除ダイアログ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'warning.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">本当に削除しますか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
            削除操作は元に戻せません。このイベントを削除した場合、参加者の情報も全て削除されます。
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" color="error">
            キャンセル
          </Button>
          <Button onClick={handleDeleteEvent} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 承認確認 */}
      <Dialog open={openApprovalDialog} onClose={() => setOpenApprovalDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'success.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">承認してよろしいでしょうか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingBottom: "20px" }}>
            PayPayID：{participantsData?.find(p => p.user_id === approveUserId)?.paypay_id}<br />
            PayPay名：{participantsData?.find(p => p.user_id === approveUserId)?.paypay_name}<br />
            {participantsData?.find(p => p.user_id === approveUserId)?.b_participation_flag?.isON() ? (<>支払額(バスケ)：{formData?.b_entry_fee}円<br /></>) : (<></>)}
            {participantsData?.find(p => p.user_id === approveUserId)?.d_participation_flag?.isON() ? (<>支払額(飲み会)：{formData?.d_entry_fee}円<br /></>) : (<></>)}
            {participantsData?.find(p => p.user_id === approveUserId)?.b_participation_flag?.isONtoOFF() ? (<>返金額(バスケ)：{formData?.b_entry_fee}円<br /></>) : (<></>)}
            {participantsData?.find(p => p.user_id === approveUserId)?.d_participation_flag?.isONtoOFF() ? (<>返金額(飲み会)：{formData?.d_entry_fee}円<br /></>) : (<></>)}
          </Box>
          <TextField
            id="approvalMessage"
            label="メッセージ"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            sx={{ whiteSpace: 'pre-line' }}
            value={approvalMessage}
            onChange={(e) => setApprovalMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setApprovalMessage('');setOpenApprovalDialog(false);}} variant="outlined" color="primary">
            キャンセル
          </Button>
          <Button onClick={handleApproval} variant="contained" color="success">
            承認
          </Button>
        </DialogActions>
      </Dialog>

      {/* 却下確認 */}
      <Dialog open={openDenyDialog} onClose={() => setOpenDenyDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'error.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">却下してよろしいでしょうか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingBottom: "20px" }}>
            PayPayID：{participantsData?.find(p => p.user_id === approveUserId)?.paypay_id}<br />
            PayPay名：{participantsData?.find(p => p.user_id === approveUserId)?.paypay_name}
          </Box>
          <TextField
            id="denyMessage"
            label="メッセージ"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            sx={{ whiteSpace: 'pre-line' }}
            value={denyMessage}
            onChange={(e) => setDenyMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setDenyMessage('');setOpenDenyDialog(false);}} variant="outlined" color="primary">
            キャンセル
          </Button>
          <Button onClick={handleDeny} variant="contained" color="error">
            却下
          </Button>
        </DialogActions>
      </Dialog>

      {/* 繰り上がり通知確認 */}
      <Dialog open={openAdvanceNoticeDialog} onClose={() => setOpenAdvanceNoticeDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'success.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">繰り上がり通知してもよろしいでしょうか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            id="advanceNotifyMessage"
            label="メッセージ"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            sx={{ whiteSpace: 'pre-line' }}
            value={advanceNotifyMessage}
            onChange={(e) => setAdvanceNoticeMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setAdvanceNoticeMessage('');setOpenAdvanceNoticeDialog(false);}} variant="outlined" color="primary">
            キャンセル
          </Button>
          <Button onClick={handleAdvanceNotice} variant="contained" color="success">
            通知する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 公開ダイアログ */}
      <Dialog open={openPublicSwitchDialog} onClose={() => setOpenPublicSwitchDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'warning.main',
            }}
          >
            <WarningAmberIcon fontSize="large" />
            <Typography variant="h6">{formData.public_flag ? '非公開に' : '公開'}しますか？</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
            {formData.public_flag
                ? '非公開にするとユーザーからはこのイベントは見えなくなり、受け付けることができなくなります。'
                : '公開すると全てのユーザーにこのイベントが公開され、参加受付が始まります。'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPublicSwitchDialog(false)} variant="outlined" color="primary">
            キャンセル
          </Button>
          <Button onClick={handlePublicSwitchEvent} variant="contained" color="primary">
            {formData.public_flag ? '非公開にする' : '公開する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 募集配信ダイアログ */}
      <Dialog open={openPublishDialog} onClose={() => setOpenPublishDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'success.main',
            }}
          >
            <CheckCircleOutlineIcon fontSize="large" />
            <Typography variant="h6">募集配信</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ marginBottom: '15px' }}>LINEで配信したいメッセージを記入してください。</Typography>
          <TextField
            id="publishMessage"
            label="メッセージ"
            variant="outlined"
            defaultValue="【新規イベント公開】以下のイベントの予約受付が開始しました！参加をご希望の方は予約システムにログインし、予約を行ってください。"
            fullWidth
            multiline
            rows={4}
            sx={{ whiteSpace: 'pre-line' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPublishDialog(false)} variant="outlined" color="error">
            キャンセル
          </Button>
          <Button onClick={handleBroadcastEvent} variant="contained" color="success">
            配信
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

    {/* 支払い明細  */}
    <Dialog open={openPaymentDetailDialog} onClose={() => setOpenPaymentDetailDialog(false)}>
      <Box
        sx={{
          p: 3,
          bgcolor: '#f8f3f8',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 2, color: '#6d4c41', fontWeight: 'bold' }}
        >
          支出明細
        </Typography>
        <Table sx={{ width: '100%', maxWidth: 600 }}>
          <TableBody>
            {paymentRows?.map((row, index) => (
              <TableRow key={index} sx={{ '& td': { border: 0 } }}>
                <TableCell sx={{ width: '10%', textAlign: 'center', fontWeight: 'bold' }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ width: '45%' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="内訳"
                    value={row.description}
                    onChange={(e) =>
                      handleInputChange(index, 'description', e.target.value)
                    }
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: '45%' }}>
                  <TextField
                    type="number"
                    fullWidth
                    variant="outlined"
                    label="金額"
                    value={row.amount}
                    onChange={(e) =>
                      handleInputChange(index, 'amount', e.target.value)
                    }
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: '10%', textAlign: 'center', fontWeight: 'bold' }}>
                  <IconButton onClick={() => {setPaymentRows(paymentRows.filter((_, i) => i !== index));}} sx={{ color: 'error.main' }}>
                    <RemoveCircleOutline fontSize="large" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: 600,
          }}
        >
          <IconButton
            onClick={handleAddPaymentRow}
            sx={{ color: 'success.main' }}
          >
            <AddCircleOutlineIcon fontSize="large" />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handlePaymentSave}
              sx={{
                bgcolor: '#81c784',
                '&:hover': { bgcolor: '#66bb6a' },
              }}
            >
              保存
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => {setOpenPaymentDetailDialog(false);setPaymentRows(paymentRowsBk);}}
              sx={{
                color: '#e57373',
                borderColor: '#e57373',
                '&:hover': {
                  bgcolor: '#ffebee',
                  borderColor: '#ef5350',
                },
              }}
            >
              閉じる
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>

    {/* 処理結果通知ダイアログ */}
    <Dialog open={openResultDialog} onClose={() => setOpenResultDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
            }}
          >
            <InfoIcon fontSize="large" />
            <Typography variant="h6">処理結果</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <pre>{processResultMessage}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setOpenResultDialog(false);setReloadTime(new Date())}} variant="contained" color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
